import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { Form, Typography } from 'antd';
import { FormProps } from 'antd/lib';
import React from 'react';
import AppRateInput from '@nxsys/components/AppRateInput';

type HolidayPayDetailFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: HolidayPayDetailFilterValues) => void;
  isBureau?: boolean;
};

export type HolidayPayDetailFilterValues = {
  payFrequency: string;
  agency: Option<string> | null;
  taxPeriodFrom: number;
  taxPeriodTo: number;
};

const HolidayPayDetailFilterModal: React.FC<
  HolidayPayDetailFilterModalProps
> = ({ isOpen, toggle, onFilter, isBureau }: HolidayPayDetailFilterModalProps) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const ableToClearFilter =
    watchValue && Object.keys(watchValue).some((key) => !!watchValue[key]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    onFilter(values);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item label={messages['common.payFrequency']} name="payFrequency">
          <AppPayFrequencySelect
            placeholder={messages['form.pleaseSelect']}
            onChange={(value) => {
              form.setFieldsValue({
                payFrequency: value,
              });
            }}
          />
        </Form.Item>

        <Typography.Text>{messages['common.taxPeriod']}</Typography.Text>
        <div className="grid grid-cols-2  gap-x-4 mt-2">
          <Form.Item name="taxPeriodFrom">
            <AppRateInput placeholder={messages['common.from']} />
          </Form.Item>
          <Form.Item name="taxPeriodTo">
            <AppRateInput placeholder={messages['common.to']} />
          </Form.Item>
        </div>

        {isBureau ? (
          <Form.Item label={messages['common.employer']} name="company">
            <AppSelectSync
              type="company"
              apiType="searchCompany"
              placeholder={messages['form.pleaseSelect']}
              onChangeValue={(value) => {
                form.setFieldsValue({
                  company: value,
                });
              }}
            />
          </Form.Item>
        ) : (
          <Form.Item label={messages['common.primaryAgency']} name="agency">
            <AppSelectSync
              type="agency"
              apiType="searchAgency"
              placeholder={messages['form.pleaseSelect']}
              onChangeValue={(value) => {
                form.setFieldsValue({
                  agency: value,
                });
              }}
            />
          </Form.Item>
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default HolidayPayDetailFilterModal;
